// i18n.js
import { createContext, useContext, useState, useEffect } from 'react';

// Translation object for English and French
const translations = {
  en: {
    // Navbar translations
    highlights: "Highlights",
    ourGames: "Our Games",
    contact: "Contact",
    altText: "Summit Games",
    // Hero translations
    title: "Card Games Collection",
    description: "Challenge players worldwide or test your skills against our advanced AI in our collection of classic card games.",
    appStore: "App Store",
    googlePlay: "Google Play",
    // Features translations
    featuresTitle: "Highlights",
    featuresDescription: "We use the latest technologies to deliver the best experience you could have on an online card game.",
    onlineModeTitle: "Online Mode",
    onlineModeDescription: "Simply the best performing online card game. As fast as lightning. Thousands of players all the time. Fast matchmaking.",
    aiTitle: "Artificial Intelligence",
    aiDescription: "Built with the most sophisticated algorithm, we challenge you to beat our artificial intelligence.",
    rankingsTitle: "Rankings",
    rankingsDescription: "Measure yourself with our worldwide real-time ranking, or see who's the best between your friends directly onto GameCenter.",
    // Footer translations
    copyright: "© {year} Summit Games. All rights reserved.",
    privacyPolicy: "Privacy Policy",
    contactLabel: "Contact",
  },
  fr: {
    // Navbar translations
    highlights: "Points forts",
    ourGames: "Nos jeux",
    contact: "Contact",
    altText: "Jeux Summit",
    // Hero translations
    title: "Collection de jeux de cartes",
    description: "Défiez des joueurs du monde entier ou testez vos compétences contre notre IA avancée dans notre collection de jeux de cartes classiques.",
    appStore: "App Store",
    googlePlay: "Google Play",
    // Features translations
    featuresTitle: "Points forts",
    featuresDescription: "Nous utilisons les dernières technologies pour offrir la meilleure expérience possible sur un jeu de cartes en ligne.",
    onlineModeTitle: "Mode en ligne",
    onlineModeDescription: "Tout simplement le meilleur jeu de cartes en ligne en termes de performance. Rapide comme l'éclair. Des milliers de joueurs en permanence. Mise en relation rapide.",
    aiTitle: "Intelligence artificielle",
    aiDescription: "Construite avec l'algorithme le plus sophistiqué, nous vous mettons au défi de battre notre intelligence artificielle.",
    rankingsTitle: "Classements",
    rankingsDescription: "Mesurez-vous avec notre classement mondial en temps réel, ou voyez qui est le meilleur parmi vos amis directement sur GameCenter.",
    // Footer translations
    copyright: "© {year} Summit Games. Tous droits réservés.",
    privacyPolicy: "Politique de confidentialité",
    contactLabel: "Contact",
  },
};

// Function to detect browser language
const getBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.languages[0];
  return browserLang.startsWith('fr') ? 'fr' : 'en';
};

// Language Context
const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(getBrowserLanguage);

  useEffect(() => {
    setLanguage(getBrowserLanguage());
  }, []);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'en' ? 'fr' : 'en'));
  };

  const t = (key, params = {}) => {
    let text = translations[language][key] || key;
    Object.keys(params).forEach((param) => {
      text = text.replace(`{${param}}`, params[param]);
    });
    return text;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};